import React, { useState, useEffect } from 'react';
import { Nav, NavItem, NavLink, TabContent, TabPane, Input, Label } from 'reactstrap';
import { getOrganizerCurrency } from '../../../../utils/currency';

// Fee Breakdown Content Component
export const FeeBreakdownContent: React.FC<{ fieldValue: any }> = ({ fieldValue }) => {
    const currencySymbol = getOrganizerCurrency().symbol;
    const [activeTab, setActiveTab] = useState('attendee');
    const [ticketPrice, setTicketPrice] = useState(10.00);
    const [ticketCount, setTicketCount] = useState(1);

    const toggleTab = (tab: string) => {
        if (activeTab !== tab) setActiveTab(tab);
    };

    const getOrganizerFees = () => {
        try {
            const orgDataStr = localStorage.getItem('organizerData');
            if (orgDataStr) {
                const orgData = JSON.parse(orgDataStr);
                return {
                    ticketFee: parseFloat(orgData.ticketFee || '0.00'),
                    processingFeePercent: parseFloat(orgData.processingFee || '0.00') / 100
                };
            }
        } catch (error) {
            console.error('Error parsing organizerData for fees:', error);
        }
        return { ticketFee: 0.00, processingFeePercent: 0.00 };
    };

    const { ticketFee: ticketingFeePerTicket, processingFeePercent } = getOrganizerFees();

    // Calculate fees based on ticket price and count
    const calculateFees = (price: number, count: number) => {
        const totalTicketPrice = price * count;
        const totalTicketingFee = ticketingFeePerTicket * count;
        const totalProcessingFee = totalTicketPrice * processingFeePercent;
        const totalFees = totalTicketingFee + totalProcessingFee;
        const payout = totalTicketPrice - totalFees;
        const attendeeCost = activeTab === 'attendee' ? (totalTicketPrice+totalFees) : (totalTicketPrice-totalFees);
        
        return {
            ticketingFeePerTicket,
            totalTicketingFee,
            totalProcessingFee,
            totalFees,
            payout,
            attendeeCost,
            totalTicketPrice
        };
    };

    const fees = calculateFees(ticketPrice, ticketCount);

    return (
        <div className="fee-breakdown-content">
            <div className="description mb-4">
                <p>Understanding how ticketing fees work and who pays them.</p>
            </div>

            {/* Ticket Price and Count Inputs */}
            <div className="row mb-4">
                <div className="col-md-6">
                    <div className="ticket-price-input">
                        <Label for="ticketPrice" className="form-label fw-bold">Ticket Price</Label>
                        <div className="input-group">
                            <span className="input-group-text">{currencySymbol}</span>
                            <Input
                                id="ticketPrice"
                                type="number"
                                value={ticketPrice}
                                onChange={(e) => setTicketPrice(parseFloat(e.target.value) || 0)}
                                min="0"
                                step="0.01"
                                className="form-control"
                                placeholder="Enter ticket price"
                            />
                        </div>
                    </div>
                </div>
                <div className="col-md-6">
                    <div className="ticket-count-input">
                        <Label for="ticketCount" className="form-label fw-bold">Number of Tickets</Label>
                        <Input
                            id="ticketCount"
                            type="number"
                            value={ticketCount}
                            onChange={(e) => setTicketCount(parseInt(e.target.value) || 1)}
                            min="1"
                            max="1000"
                            className="form-control"
                            placeholder="Enter number of tickets"
                        />
                    </div>
                </div>
            </div>

            {/* Tabs Navigation */}
            <Nav tabs className="mb-4">
                <NavItem>
                    <NavLink
                        className={activeTab === 'attendee' ? 'active' : ''}
                        onClick={() => toggleTab('attendee')}
                    >
                        <span className="fw-bold">Attendee Pays Fees</span>
                    </NavLink>
                </NavItem>
                <NavItem>
                    <NavLink
                        className={activeTab === 'organizer' ? 'active' : ''}
                        onClick={() => toggleTab('organizer')}
                    >
                        <span className="fw-bold">Organizer Absorbs Fees</span>
                    </NavLink>
                </NavItem>
            </Nav>

            <TabContent activeTab={activeTab}>
                <TabPane tabId="attendee">
                    <div className="fee-breakdown-section">
                        {/* Cost to Buyer */}
                        <div className="fee-row mb-3">
                            <div className="row align-items-center">
                                <div className="col-6">
                                    <div className="fee-label fw-bold text-muted">Cost to buyer</div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="fee-value fw-bold">{currencySymbol}{fees.attendeeCost.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Ticket Price */}
                        <div className="fee-row mb-3">
                            <div className="row align-items-center">
                                <div className="col-6">
                                    <div className="fee-label fw-bold text-muted">Ticket price ({ticketCount} × {currencySymbol}{ticketPrice.toFixed(2)})</div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="fee-value fw-bold">{currencySymbol}{fees.totalTicketPrice.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Ticketing Fee */}
                        <div className="fee-row mb-2">
                            <div className="row align-items-center">
                                <div className="col-6">
                                    <div className="fee-label">Ticketing fee</div>
                                    <div className="fee-description text-muted small">{currencySymbol}{ticketingFeePerTicket.toFixed(2)} per ticket ({ticketCount} tickets)</div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="fee-value text-success">+ {currencySymbol}{fees.totalTicketingFee.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Processing Fee */}
                        <div className="fee-row mb-3">
                            <div className="row align-items-center">
                                <div className="col-6">
                                    <div className="fee-label">Processing fee</div>
                                    <div className="fee-description text-muted small">{parseFloat((processingFeePercent * 100).toFixed(4))}% per order</div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="fee-value text-success">+ {currencySymbol}{fees.totalProcessingFee.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Payout Section */}
                        <div className="payout-section p-3 bg-light rounded mb-3">
                            <div className="row align-items-center">
                                <div className="col-6">
                                    <div className="d-flex align-items-center">
                                        <div className="payout-icon me-2">
                                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="11" cy="11" r="11" fill="#3659E3"/>
                                                <path fillRule="evenodd" clipRule="evenodd" d="M9.28566 8.83333C9.28566 8.10667 9.96913 7.6 11.1128 7.6C12.3173 7.6 12.7639 8.16667 12.8045 9H14.3001C14.2527 7.85333 13.082 6.74134 11.6677 6.40134V5H10.3123V6.40134C8.99946 6.68134 7.72923 7.56 7.72923 8.84667C7.72923 10.3867 9.02174 11.1533 10.9098 11.6C12.6015 12 12.9399 12.5867 12.9399 13.2067C12.9399 13.6667 12.6083 14.4 11.1128 14.4C9.71875 14.4 9.17062 13.7867 9.09618 13H7.60742C7.68863 14.46 9.01497 15.28 10.3143 15.5533V17H11.6677V15.5667C12.9872 15.32 14.4963 14.5667 14.4963 13.2C14.4963 11.3067 12.8519 10.66 11.3158 10.2667C9.77965 9.87333 9.28566 9.46667 9.28566 8.83333Z" fill="white"/>
                                            </svg>
                                        </div>
                                        <span className="fw-bold">Your payout</span>
                                    </div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="fee-value fw-bold">{currencySymbol}{fees.totalTicketPrice.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </TabPane>

                <TabPane tabId="organizer">
                    <div className="fee-breakdown-section">
                        {/* Cost to Buyer */}
                        <div className="fee-row mb-3">
                            <div className="row align-items-center">
                                <div className="col-6">
                                    <div className="fee-label fw-bold text-muted">Cost to buyer</div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="fee-value fw-bold">{currencySymbol}{fees.totalTicketPrice.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Ticket Price */}
                        <div className="fee-row mb-3">
                            <div className="row align-items-center">
                                <div className="col-6">
                                    <div className="fee-label fw-bold text-muted">Ticket price ({ticketCount} × {currencySymbol}{ticketPrice.toFixed(2)})</div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="fee-value fw-bold">{currencySymbol}{fees.totalTicketPrice.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Ticketing Fee */}
                        <div className="fee-row mb-2">
                            <div className="row align-items-center">
                                <div className="col-6">
                                    <div className="fee-label">Ticketing fee</div>
                                    <div className="fee-description text-muted small">{currencySymbol}{ticketingFeePerTicket.toFixed(2)} per ticket ({ticketCount} tickets)</div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="fee-value text-danger">- {currencySymbol}{fees.totalTicketingFee.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Processing Fee */}
                        <div className="fee-row mb-3">
                            <div className="row align-items-center">
                                <div className="col-6">
                                    <div className="fee-label">Processing fee</div>
                                    <div className="fee-description text-muted small">{parseFloat((processingFeePercent * 100).toFixed(4))}% per order</div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="fee-value text-danger">- {currencySymbol}{fees.totalProcessingFee.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>

                        {/* Payout Section */}
                        <div className="payout-section p-3 bg-light rounded mb-3">
                            <div className="row align-items-center">
                                <div className="col-6">
                                    <div className="d-flex align-items-center">
                                        <div className="payout-icon me-2">
                                            <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <circle cx="11" cy="11" r="11" fill="#3659E3"/>
                                                <path fillRule="evenodd" clipRule="evenodd" d="M9.28566 8.83333C9.28566 8.10667 9.96913 7.6 11.1128 7.6C12.3173 7.6 12.7639 8.16667 12.8045 9H14.3001C14.2527 7.85333 13.082 6.74134 11.6677 6.40134V5H10.3123V6.40134C8.99946 6.68134 7.72923 7.56 7.72923 8.84667C7.72923 10.3867 9.02174 11.1533 10.9098 11.6C12.6015 12 12.9399 12.5867 12.9399 13.2067C12.9399 13.6667 12.6083 14.4 11.1128 14.4C9.71875 14.4 9.17062 13.7867 9.09618 13H7.60742C7.68863 14.46 9.01497 15.28 10.3143 15.5533V17H11.6677V15.5667C12.9872 15.32 14.4963 14.5667 14.4963 13.2C14.4963 11.3067 12.8519 10.66 11.3158 10.2667C9.77965 9.87333 9.28566 9.46667 9.28566 8.83333Z" fill="white"/>
                                            </svg>
                                        </div>
                                        <span className="fw-bold">Your payout</span>
                                    </div>
                                </div>
                                <div className="col-6 text-end">
                                    <div className="fee-value fw-bold">{currencySymbol}{fees.payout.toFixed(2)}</div>
                                </div>
                            </div>
                        </div>
                    </div>
                </TabPane>
            </TabContent>
        </div>
    );
};